/* cf-enduro-label.js — Click Fencing, 2026-09-02, coating on every glass line 2026-09-14
 *
 * WHY THIS EXISTS
 * Pete ticked "EnduroShield Coating" on the glass calculator and saw nothing
 * change anywhere, and reasonably asked whether it was even charging for it.
 *
 * It was. Measured on the live theme: the calculator sets
 * state.shared.enduroShield, swaps to the coated variants (panels 49563-49601,
 * raked 49843-49858) and bills the coated price. Nothing was mispriced and
 * nothing was misordered - the cart carried the genuine coated variant ids.
 *
 * The defect was that the word "EnduroShield" appeared NOWHERE once the box was
 * ticked. The 2026-09-02 version of this file fixed that on screen only.
 *
 * 2026-09-14 - EVERY GLASS LINE NOW STATES ITS COATING, IN THE QUOTE DATA.
 * Pete: "even on the line items in his email it doesnt state endurosheild or not.
 * this needs to be a site wide modification".
 * Measured on preview 141736411230 the same day (EnduroShield, 6000mm, 900 gate, rake):
 *   state.quoteBreakdown  "1250mm Glass Panel"          <- no coating (screen only)
 *                         "Left Rake Panel (1500mm)"    <- no coating anywhere
 *                         "Gate Panel 900 x 1200mm"     <- no coating anywhere
 * The saved quote, quote-review, the plan, the plan PDF, the enquiry bridge and the
 * cart note are all built from that data, not from the screen, so none of them said
 * the panels were coated, and nothing anywhere said a plain job was plain.
 *
 * WHAT IT DOES NOW. Wraps _calcSectionQuote and appends the coating to the NAME of
 * every glass line, so everything downstream carries it:
 *   "<w>mm Glass Panel(s)"      , EnduroShield Coated  |  , Standard Clear (Uncoated)
 *   "Left/Right Rake Panel (h)" , EnduroShield Coated  |  , Standard Clear (Uncoated)
 *   "<w>mm Gate Hinge Panel"    , Standard Clear (Uncoated)   - a coated hinge panel is
 *        already named "..., EnduroShield Coated" by cf-enduro-hinge-panel.js; any hinge
 *        line WITHOUT that name is the plain part (48210-48222 range), so it says so.
 *   "Gate Panel <w> x 1200mm"   , Standard Clear (Uncoated)   - same rule as the hinge panel:
 *        on an EnduroShield section cf-enduro-hinge-panel.js swaps the 8mm gate to the coated
 *        8mm gate (49602-49604, Pete 14 Sep 2026) and names it "..., EnduroShield Coated".
 *        A gate line without that name is the clear 8mm gate 48220-48222 and says so. Which
 *        gate part is chosen is NOT decided here.
 * Not labelled: "Section N Glass Panels" (the per-lineal-metre fallback when a layout
 * cannot be built - it is a flat rate with no product and no coating priced in), and
 * every hardware line.
 *
 * Names only. Never a price, a quantity, a variantId or a sku. Consumers checked
 * 2026-09-14: cf-panel-variants.js (width AND price), cf-quote-truth.js (/enduroshield/
 * decides coated - the uncoated wording deliberately does not contain that word),
 * cf-plans.js (/enduroshield/ in line names), ullc-catalogue-fix.js and ullc-calc.js
 * (/^(\d+)mm Gate Hinge Panel/, prefix only), cf-plan-autosave.js ('Hinge Panel'),
 * cf-calc-hinges-plural (Hinge not followed by Panel), quote-review.js (merge key uses
 * the whole name, identical within a job).
 *
 * ORDER MATTERS. cf-enduro-hinge-panel.js matches /^(\d+)mm Gate Hinge Panel\s*$/ - anchored
 * at the end - so this wrapper must sit OUTSIDE it (installed after it), or a coated job's
 * hinge panel would be labelled uncoated before it could be corrected. The installer waits
 * for that file's flag when the file is on the page.
 *
 * Glass is the one calculator whose instance is reachable (window.glassCalculator), so this
 * wraps prototype methods directly. Every wrapper is in try/catch: a label must never be able
 * to break a quote.
 */
(function () {
  'use strict';

  var TAG = '[cf-enduro-label]';
  var LABEL = 'EnduroShield Coated';
  var UNCOATED = 'Standard Clear (Uncoated)';
  var MAX_TRIES = 60;
  var tries = 0;

  /* A name that already states its coating - by this file, cf-enduro-hinge-panel.js, or a
     saved quote. Never append twice. */
  var STATED_RE = /EnduroShield|\(Uncoated\)/i;

  /* Line names exactly as _calcSectionQuote builds them (ullc-calc.js ~13947-14091). */
  var DATA_PANEL_RE = /^\d+mm Glass Panels?$/;
  var DATA_RAKE_RE  = /^(?:Left|Right) Rake Panel \(\d+mm\)$/;
  var DATA_HINGE_RE = /^\d+mm Gate Hinge Panel$/;
  var DATA_GATE_RE  = /^Gate Panel \d+ x 1200mm$/;

  /* "1450mm Glass Panels", "1500mm Glass Panel", "1500mm Glass Raked Panel".
     Group 1 is the part we append to; group 2 keeps whatever trails it. */
  var PANEL_RE = /^(\s*\d+\s*mm\s+Glass\s+(?:Raked\s+)?Panels?)(\b[\s\S]*)$/i;

  function calc() {
    return window.glassCalculator || window.glassFencingCalculator || null;
  }

  function isOn() {
    try {
      var c = calc();
      return !!(c && c.state && c.state.shared && c.state.shared.enduroShield);
    } catch (e) { return false; }
  }

  /* Same test cf-enduro-hinge-panel.js uses, so the two can never disagree about a section. */
  function sectionIsEnduro(self, section) {
    try {
      if (self && typeof self.resolveSectionFinish === 'function') {
        return !!self.resolveSectionFinish(section).enduroShield;
      }
    } catch (e) {}
    return !!(section && section.enduroShield);
  }

  /* ---- The quote data: every glass line names its coating ------------------ */
  function coatingFor(item, enduro) {
    if (typeof item !== 'string' || STATED_RE.test(item)) return null;
    var name = item.trim();
    if (DATA_GATE_RE.test(name))  return UNCOATED;                 // 8mm clear leaf, by design
    if (DATA_HINGE_RE.test(name)) return UNCOATED;                 // coated ones are already named
    if (DATA_PANEL_RE.test(name) || DATA_RAKE_RE.test(name)) return enduro ? LABEL : UNCOATED;
    return null;
  }

  function nameLines(self, section, quote) {
    if (!quote || !Array.isArray(quote.breakdown)) return;
    var enduro = sectionIsEnduro(self, section);
    quote.breakdown.forEach(function (line) {
      try {
        if (!line) return;
        var suffix = coatingFor(line.item, enduro);
        if (suffix) line.item = String(line.item).trim() + ', ' + suffix;
      } catch (e) { console.warn(TAG, 'name', e); }
    });
  }

  function hingeFixReady(proto) {
    if (proto.__cfEnduroHingePanelPatched) return true;
    try {
      return !document.querySelector('script[src*="cf-enduro-hinge-panel"]');
    } catch (e) { return true; }
  }

  var nameTries = 0;
  function installNames() {
    var c = calc();
    var proto = c && Object.getPrototypeOf(c);
    if (!proto || typeof proto._calcSectionQuote !== 'function') return false;
    if (proto.__cfCoatingNamesPatched) return true;
    /* Wait (up to ~15s) for the hinge-panel correction to wrap first; then go regardless,
       because a name that states the coating is better than none. */
    if (!hingeFixReady(proto) && nameTries < 150) return false;
    var orig = proto._calcSectionQuote;
    proto._calcSectionQuote = function (sectionNum, section) {
      var quote = orig.apply(this, arguments);
      try { nameLines(this, section, quote); } catch (e) { console.warn(TAG, 'names', e); }
      return quote;
    };
    proto.__cfCoatingNamesPatched = true;
    try {
      console.log(TAG, 'coating names installed' +
        (proto.__cfEnduroHingePanelPatched ? ' (after hinge-panel fix)' : ''));
    } catch (e) {}
    return true;
  }

  (function waitNames() {
    nameTries++;
    var ok = false;
    try { ok = installNames(); } catch (e) { console.warn(TAG, 'install names', e); }
    if (!ok && nameTries < 400) setTimeout(waitNames, 100);
  })();

  /* ---- "Selected Components" gets its own ticked line -------------------- */
  function summaryLine(on) {
    try {
      var boxes = document.querySelectorAll('.components-summary');
      for (var i = 0; i < boxes.length; i++) {
        var box = boxes[i];
        var existing = box.querySelector('[data-cf-enduro]');
        if (on && !existing) {
          var p = document.createElement('p');
          p.setAttribute('data-cf-enduro', '1');
          p.textContent = '✓ Glass: EnduroShield Coated';
          box.appendChild(p);
        } else if (!on && existing) {
          existing.parentNode.removeChild(existing);
        }
      }
    } catch (e) { console.warn(TAG, 'summary', e); }
  }

  /* ---- Breakdown line names on screen ------------------------------------ */
  /* Since 2026-09-14 the names arrive from the data already stating the coating, so this is
     only the fallback for a line that does not. It must never touch a name that states a
     coating: its regex would happily append a second one. */
  function lineNames(on) {
    try {
      var names = document.querySelectorAll('.breakdown-item .item-name');
      for (var i = 0; i < names.length; i++) {
        var el = names[i];
        var saved = el.getAttribute('data-cf-orig');
        var cur = el.textContent;
        if (saved !== null) {
          /* A row element can be reused and re-filled from the data
             (cf-current-section-parts.js). If its text is no longer ours, forget it. */
          var ms = PANEL_RE.exec(saved);
          var ours = ms ? ms[1] + ', ' + LABEL + ms[2] : null;
          if (cur !== saved && cur !== ours) { el.removeAttribute('data-cf-orig'); saved = null; }
        }
        if (saved === null && STATED_RE.test(cur)) continue;
        var orig = (saved === null) ? cur : saved;
        var m = PANEL_RE.exec(orig);
        if (!m) continue;
        if (on) {
          if (saved === null) el.setAttribute('data-cf-orig', orig);
          var want = m[1] + ', ' + LABEL + m[2];
          if (el.textContent !== want) el.textContent = want;
        } else if (saved !== null) {
          if (el.textContent !== orig) el.textContent = orig;
          el.removeAttribute('data-cf-orig');
        }
      }
    } catch (e) { console.warn(TAG, 'lines', e); }
  }

  function decorate() {
    var on = isOn();
    summaryLine(on);
    lineNames(on);
  }

  /* decorate() mutates the DOM and the observer watches the DOM, so it must
     settle rather than loop. It does: every write is guarded by a comparison,
     so the second pass writes nothing and the observer stops firing. The
     scheduled flag is reset INSIDE the timeout - never left true on an error
     path, which is how a re-entry guard deadlocks. */
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      try { decorate(); } catch (e) { console.warn(TAG, 'decorate', e); }
    }, 60);
  }

  /* ---- Cart item titles -------------------------------------------------- */
  /* Shopify prices from the variantId, so this only changes what the customer
     reads in the calculator's own list. The variant is already the coated one.
     (state.cartItems is not on the money path - the real cart is built from the saved
     quote's breakdown, which now carries the coating in its names.) */
  function relabelCart(items) {
    try {
      if (!isOn() || !items || !items.length) return;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it || typeof it.title !== 'string') continue;
        if (STATED_RE.test(it.title)) continue;
        var m = PANEL_RE.exec(it.title);
        if (m) it.title = m[1] + ', ' + LABEL + m[2];
      }
    } catch (e) { console.warn(TAG, 'cart', e); }
  }

  function install() {
    var c = calc();
    if (!c) return false;
    var proto = Object.getPrototypeOf(c);
    if (!proto) return false;
    if (proto.__cfEnduroLabelPatched) return true;

    if (typeof proto._buildCartItemsForSection === 'function') {
      var origCart = proto._buildCartItemsForSection;
      proto._buildCartItemsForSection = function () {
        var out = origCart.apply(this, arguments);
        try { relabelCart(this.state && this.state.cartItems); } catch (e) { console.warn(TAG, e); }
        return out;
      };
    }

    ['updateQuoteBreakdown', 'updateQuoteDisplay', 'updateQuoteElements'].forEach(function (m) {
      if (typeof proto[m] !== 'function') return;
      var orig = proto[m];
      proto[m] = function () {
        var out = orig.apply(this, arguments);
        try { schedule(); } catch (e) { console.warn(TAG, e); }
        return out;
      };
    });

    proto.__cfEnduroLabelPatched = true;

    /* The summary and the breakdown are also re-rendered by paths not wrapped
       above (step navigation, restoring a saved plan), so watch the page too. */
    try {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    } catch (e) { console.warn(TAG, 'observer', e); }

    /* Ticking the box on step 1 must repaint step 3 when the customer returns. */
    document.addEventListener('change', function (e) {
      if (e && e.target && e.target.id === 'glass-type') schedule();
    }, true);

    decorate();
    console.log(TAG, 'installed');
    return true;
  }

  (function tryInstall() {
    tries++;
    var ok = false;
    try { ok = install(); } catch (e) { console.warn(TAG, 'install failed', e); }
    if (!ok && tries < MAX_TRIES) setTimeout(tryInstall, 500);
  })();
})();
